import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import api from '../../lib/api';
import { lotPaymentsAPI } from '../../lib/lotPaymentsApi';
import { messageErreur } from '../../lib/erreurs';
import { creerGardeCle, nombreSaisi } from '../../lib/transportStatements';
import { jourDuJour } from '../../lib/lotsFiltres';
import { symboleDevise } from '../../lib/formats';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';

const formVide = () => ({ montant: '', compte: '', date: jourDuJour(), note: '' });

/** Les comptes de trésorerie de la liste du serveur, quelle que soit l'enveloppe. */
const comptesDe = (reponse) => {
  const corps = reponse?.data?.data ?? reponse?.data;
  if (Array.isArray(corps)) return corps;
  return Array.isArray(corps?.items) ? corps.items : [];
};

/**
 * « Verser un acompte » : un paiement au partenaire du lot, depuis un compte de
 * trésorerie. La même demande rejouée garde sa clé d'idempotence : un envoi
 * repris après une coupure ne crée pas un second acompte.
 */
export const AcompteDialog = ({ open, onOpenChange, lotId, reference, devise = 'XOF', onDone }) => {
  const [form, setForm] = useState(formVide);
  const [comptes, setComptes] = useState([]);
  const [tentative, setTentative] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const garde = useRef(creerGardeCle('lot-acompte'));
  // L'argent attend le serveur : bouton inactif sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  useEffect(() => {
    if (!open) return undefined;
    setForm(formVide());
    setTentative(false);
    setEnCours(false);
    let actif = true;
    api
      .get('/finance/accounts')
      .then((reponse) => {
        if (actif) setComptes(comptesDe(reponse));
      })
      .catch(() => {
        if (actif) setComptes([]);
      });
    return () => {
      actif = false;
    };
  }, [open]);

  // Un compte dans une autre devise que le lot ne peut pas le payer.
  const options = useMemo(
    () =>
      comptes
        .filter((compte) => compte?.id && (!compte.currency || compte.currency === devise))
        .map((compte) => ({
          value: compte.id,
          label: `${compte.name || 'Compte sans nom'} · ${symboleDevise(compte.currency || devise)}`,
        })),
    [comptes, devise],
  );

  const changer = (nom, valeur) => setForm((precedent) => ({ ...precedent, [nom]: valeur }));
  const montant = nombreSaisi(form.montant);
  const erreurs = { montant: !(montant > 0), compte: !form.compte };

  const envoyer = async (evenement) => {
    evenement.preventDefault();
    if (enCours) return;
    setTentative(true);
    if (erreurs.montant || erreurs.compte) {
      toast.error('Complétez les champs signalés');
      return;
    }
    setEnCours(true);
    try {
      const corps = {
        amount: montant,
        account_id: form.compte,
        date: form.date || undefined,
        note: form.note.trim() || undefined,
      };
      const resultat = await lotPaymentsAPI.recordAdvance(lotId, corps, garde.current({ lotId, corps }));
      // L'acompte est enregistré: un autre acompte identique (même montant, même compte, même jour)
      // est un nouvel acompte, pas un rejeu; il ne doit pas reprendre cette clé.
      garde.current = creerGardeCle('lot-acompte');
      toast.success('Acompte enregistré');
      onOpenChange(false);
      onDone?.(resultat);
    } catch (err) {
      toast.error(messageErreur(err, 'Enregistrement de l’acompte impossible'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(ouvert) => !enCours && onOpenChange(ouvert)}>
      <DialogContent className="sm:max-w-lg" data-testid="lot-acompte-dialog">
        <DialogHeader>
          <DialogTitle>Verser un acompte{reference ? ` · ${reference}` : ''}</DialogTitle>
          <DialogDescription className="sr-only">Montant, compte de trésorerie, date et observations de l’acompte.</DialogDescription>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="acompte-montant">Montant</Label>
            <div className="relative">
              <Input
                id="acompte-montant"
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={form.montant}
                disabled={enCours}
                onChange={(evenement) => changer('montant', evenement.target.value)}
                className="pr-16 text-right tabular-nums"
                data-testid="lot-acompte-montant"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {symboleDevise(devise)}
              </span>
            </div>
            <FieldError show={tentative && erreurs.montant}>Saisissez un montant supérieur à zéro.</FieldError>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acompte-compte">Payé depuis</Label>
            <SearchableSelect
              id="acompte-compte"
              placeholder="Choisir le compte"
              searchPlaceholder="Rechercher un compte…"
              emptyMessage="Aucun compte de trésorerie"
              value={form.compte}
              onValueChange={(valeur) => changer('compte', valeur)}
              options={options}
              disabled={enCours}
              data-testid="lot-acompte-compte"
            />
            <FieldError show={tentative && erreurs.compte}>Choisissez le compte qui paie.</FieldError>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="acompte-date">Date</Label>
              <Input
                id="acompte-date"
                type="date"
                value={form.date}
                disabled={enCours}
                onChange={(evenement) => changer('date', evenement.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acompte-note">Observations</Label>
              <Input
                id="acompte-note"
                value={form.note}
                maxLength={500}
                disabled={enCours}
                onChange={(evenement) => changer('note', evenement.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !enLigne} data-testid="lot-acompte-envoyer">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Verser l’acompte
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AcompteDialog;
